// src/components/common/ImageUploader.tsx
import { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { NotFoundImage } from '../../utils/function';

interface ImageUploaderProps {
  value?: string | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function ImageUploader({
  value,
  onChange,
  disabled = false,
  placeholder = 'Choose File',
  className = '',
}: ImageUploaderProps) {
  const [preview, setPreview] = useState<string | null>(value || null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
      onChange(file);
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onChange(null);
  }, [onChange]);

  return (
    <div className={className}>
      <div className="flex items-center space-x-4">
        {preview && (
          <div className="relative group">
            <div className="w-16 h-16 rounded-lg overflow-hidden">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = NotFoundImage; // Add a default placeholder image
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <label className="cursor-pointer">
          <span className="px-4 py-2.5 bg-black/20 border border-white/10 hover:bg-white/5 text-white rounded-lg text-sm font-medium transition-colors">
            {preview ? 'Change Image' : placeholder}
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            disabled={disabled}
          />
        </label>
      </div>
    </div>
  );
}
